import React, { useCallback } from "react";
import SearchResultsWrapperDesktop from "./SearchResultsWrapper.desktop.js";
import { HiddenVisually, Stack, Title, useViewport } from "@bookingcom/bui-react";
import SearchResultBanner from "../SearchResultBanner/SearchResultBanner.js";
import useSearchResultsDesktopController from "./useSearchResults.desktop.controller.js";
import SkeletonSearchDesktop, { SkeletonSearchContent } from "../Skeleton/SkeletonPages/SkeletonSearch.desktop.js";
import FailedSearchResults from "./FailedSearchResults.desktop.js";
import { RemoveDirectFlights } from "./components/RemoveDirectFlights.js";
import { TooManyFilters } from "./components/TooManyFilters.js";
import { NoResultsFound } from "./components/NoResultsFound.js";
import SearchResultsContentDesktop from "./SearchResultsContent.desktop.js";
import SearchResultsListOutboundDesktop from "./SearchResultsList.outbound.desktop.js";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import { useHistory, useParams } from "react-router-dom";
import { t, useI18n } from "@bookingcom/lingojs-react";
import Frame from "../Frame/index.js";
import NoDirectFlightsBanner from "./components/NoDirectFlightsBanner.js";
import { DestinationUkraineWarning } from "../TravelAlert/index.js";
import SirfInfoBanner from "../SirfInfoBanner/index.js";
import { getUpdatedSearchParams } from "../../../hooks/useSearchFilters.js";
import { getInitialState } from "../../../store/search/state.js";
import SearchSortSelector from "./components/SearchSortSelector.js";
import SearchBaggagePolicies from "../SearchBaggagePolicies/index.js";
import PriceAlertToggle from "../PriceAlerts/PriceAlertToggle.js";
import { useIsPriceAlertsEligible } from "../PriceAlerts/usePriceAlertToggle.js";
import { clearSplitBoundsBrandedFareParams } from "../../../utils/splitBoundsBrandedFareParams.js";
const Header = () => {
    const i18n = useI18n();
    const { isSmall } = useViewport();
    const title = (React.createElement(Title, { titleTagName: "h2", title: i18n.trans(t("flights_sr_split_bounds_us_select_departing")), subtitle: i18n.trans(t("flights_sr_split_bounds_us_price_disclaimer")) }));
    return (React.createElement(Frame, { pb: 4 },
        React.createElement(Stack, { direction: isSmall ? "column" : "row", gap: 2, alignItems: isSmall ? "start" : "center" },
            React.createElement(Stack.Item, { grow: true }, title),
            React.createElement(SearchSortSelector, { isSmall: isSmall }))));
};
const SearchResultsOutboundDesktop = () => {
    const { getScreenReaderAnnouncementText, appliedFilters, isFirstLoad, canExtendSearch, onExpandToCitySearch, hasFlights, canRemoveDirectFlightsFilter, flights, fetchStatus, banners, isSirfAvailable, fetchedAt, resetInboundFilters } = useSearchResultsDesktopController();
    const i18n = useI18n();
    const isPriceAlertsEligible = useIsPriceAlertsEligible();
    const humanRightBanner = banners?.find((banner) => banner.id === "LEGAL_NOTICE_HUMAN_RIGHTS");
    const { createUrl } = useCreateUrl();
    const history = useHistory();
    const { airports } = useParams();
    const onClickSelectFlight = useCallback(async (flight) => {
        resetInboundFilters();
        const params = resetSortAndPageParams(getUpdatedSearchParams(getInitialState().filtersDraftInbound));
        const url = createUrl(`/flights/${airports}/inbound/${flight.outboundFlightInfo?.token}/?${params}`);
        history.push(url);
    }, [resetInboundFilters, createUrl, airports, history]);
    let resultingComp = undefined;
    if (fetchStatus === "initial" || fetchStatus === "loading") {
        if (isFirstLoad) {
            return React.createElement(SkeletonSearchDesktop, null);
        }
        resultingComp = React.createElement(SkeletonSearchContent, null);
    }
    if (fetchStatus === "failed" && canExtendSearch) {
        return React.createElement(FailedSearchResults, { onClickExpandToCitySearch: onExpandToCitySearch });
    }
    if (fetchStatus === "success") {
        if (hasFlights) {
            resultingComp = (React.createElement(SearchResultsContentDesktop, { fetchedAt: fetchedAt },
                React.createElement(NoDirectFlightsBanner, null),
                React.createElement(DestinationUkraineWarning, null),
                isSirfAvailable && (React.createElement(SirfInfoBanner, { bannerName: "search-results", bannerContent: i18n.trans(t("flights_sr_spanish_discount_banner")) })),
                React.createElement(SearchBaggagePolicies, { hideBaggagePolicies: true }),
                isPriceAlertsEligible && React.createElement(PriceAlertToggle, null),
                React.createElement(SearchResultsListOutboundDesktop, { flights: flights, onSelectFlight: onClickSelectFlight })));
        }
        else if (canRemoveDirectFlightsFilter) {
            return (React.createElement(React.Fragment, null,
                React.createElement(SearchResultBanner, { banner: humanRightBanner }),
                React.createElement(RemoveDirectFlights, null)));
        }
    }
    if (!resultingComp) {
        if (appliedFilters.length > 0) {
            resultingComp = React.createElement(TooManyFilters, null);
        }
        else {
            return React.createElement(NoResultsFound, null);
        }
    }
    return (React.createElement(SearchResultsWrapperDesktop, { banners: React.createElement(React.Fragment, null,
            humanRightBanner && React.createElement(SearchResultBanner, { banner: humanRightBanner }),
            React.createElement(Header, null)), showGenAiSummary: false },
        resultingComp,
        React.createElement(HiddenVisually, null,
            React.createElement("div", { "aria-live": "polite" }, getScreenReaderAnnouncementText()))));
};
const resetSortAndPageParams = (params) => {
    params.set("sort", "BEST");
    params.delete("page");
    params.delete("auto_load_page");
    clearSplitBoundsBrandedFareParams(params);
    return params;
};
export default React.memo(SearchResultsOutboundDesktop);
